// src/hooks/useEmployeeApi.js
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getEmployees,
  getEmployeeById,
  searchEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
} from "../lib/employeeApi";

// Query Keys Factory - for consistency
const employeeKeys = {
  all: ["employees"],
  lists: () => [...employeeKeys.all, "list"],
  list: (filters) => [...employeeKeys.lists(), filters], // e.g., { page, limit }
  details: () => [...employeeKeys.all, "detail"],
  detail: (id) => [...employeeKeys.details(), id],
};

/**
 * Hook to fetch a paginated and filtered list of employees.
 * @param {object} filters - An object containing filters like { page, limit, department }.
 */
export const useGetEmployees = (filters = {}) => {
  return useQuery({
    // CRITICAL: The filters object is part of the query key.
    // This ensures React Query caches different filtered lists separately.
    queryKey: ["employees", "list", filters],
    queryFn: () => getEmployees(filters),
    keepPreviousData: true,
  });
};

/**
 * --- NEW ABSTRACTION HOOK ---
 * A simple, descriptive hook for fetching all employees in the sales department.
 * This is used for populating dropdowns.
 */
export const useGetSalesEmployees = () => {
  // We use the powerful useGetEmployees hook with a specific filter.
  // We set a high limit to ensure we get all sales employees for the dropdown.
  const { data, ...rest } = useGetEmployees({
    department: "sales",
    limit: 100,
  });

  // Return the data in a clean format for the component to use.
  return { salesEmployees: data?.employees, ...rest };
};

/**
 * Hook to fetch a single employee's details.
 * @param {string} employeeId - The employee ID.
 */
export const useGetEmployeeById = (employeeId) => {
  return useQuery({
    queryKey: employeeKeys.detail(employeeId),
    queryFn: () => getEmployeeById(employeeId),
    enabled: !!employeeId, // Only runs the query if employeeId is not null/undefined
  });
};

/**
 * Hook to search for employees by name.
 * @param {string} searchTerm - The name to search for.
 */
export const useSearchEmployees = (searchTerm) => {
  return useQuery({
    queryKey: ["employees", "search", searchTerm],
    queryFn: () => searchEmployees(searchTerm),
    enabled: !!searchTerm && searchTerm.length > 2, // Only runs if searchTerm has 3+ characters
  });
};

/**
 * Hook to create a new employee.
 */
export const useCreateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEmployee,
    onSuccess: () => {
      // Invalidate all queries starting with 'employees' to refetch lists
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
};

/**
 * Hook to update an employee.
 */
export const useUpdateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeId, data }) => updateEmployee(employeeId, data),
    onSuccess: (data, variables) => {
      // Invalidate the general list and the specific employee detail query
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      queryClient.invalidateQueries({
        queryKey: employeeKeys.detail(variables.employeeId),
      });
    },
  });
};

/**
 * Hook to soft-delete an employee.
 */
export const useDeleteEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteEmployee,
    onSuccess: () => {
      // Invalidate all employee queries
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
};
